'use client';

/**
 * Live MapLibre map inside the poster preview.
 *
 * Replaces the abstract placeholder the editor used to draw, so a customer sees
 * their actual city in the actual style before ordering. Panning and zooming
 * frames the poster, and the resulting centre and zoom are handed back so the
 * print file is rendered on exactly what was on screen.
 */

import { useEffect, useRef } from 'react';
import type { MapWallStyleId } from '@/lib/mapwall';
import { buildMapWallMapStyle } from '@/lib/mapwall-style';

export type MapView = { lat: number; lng: number; zoom: number };

export function MapWallMap({
  lat,
  lng,
  zoom,
  styleId,
  interactive = true,
  onViewChange,
}: {
  lat: number;
  lng: number;
  zoom: number;
  styleId: MapWallStyleId;
  interactive?: boolean;
  onViewChange?: (view: MapView) => void;
}) {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<any>(null);
  const viewChange = useRef(onViewChange);
  viewChange.current = onViewChange;

  // Create once. Style and position are applied by the effects below, so a
  // style switch never tears down the map and reloads every tile.
  useEffect(() => {
    if (!container.current || map.current) return;
    let cancelled = false;

    (async () => {
      try {
      const lib = await import('maplibre-gl');
      // The package ships both a namespace and a default export depending on the
      // bundler, so accept either rather than silently failing to mount.
      const maplibregl: any = (lib as any).Map ? lib : (lib as any).default;
      if (cancelled || !container.current) return;

      const instance = new maplibregl.Map({
        container: container.current,
        style: buildMapWallMapStyle(styleId),
        center: [lng, lat],
        zoom,
        attributionControl: false,
        interactive,
      });

      if (interactive) {
        instance.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
        instance.on('moveend', () => {
          const centre = instance.getCenter();
          viewChange.current?.({ lat: centre.lat, lng: centre.lng, zoom: instance.getZoom() });
        });
      }

      instance.on('error', (event: any) => console.error('MapWall map error:', event?.error || event));
      map.current = instance;
      } catch (error) {
        console.error('MapWall map kon niet laden:', error);
      }
    })();

    return () => {
      cancelled = true;
      map.current?.remove();
      map.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map.current) return;
    map.current.setStyle(buildMapWallMapStyle(styleId));
  }, [styleId]);

  useEffect(() => {
    if (!map.current) return;
    const centre = map.current.getCenter();
    const moved = Math.abs(centre.lat - lat) > 1e-6 || Math.abs(centre.lng - lng) > 1e-6;
    const zoomed = Math.abs(map.current.getZoom() - zoom) > 0.01;
    // Only fly when the change came from outside (a new search), otherwise the
    // map would fight the user's own panning.
    if (moved || zoomed) map.current.jumpTo({ center: [lng, lat], zoom });
  }, [lat, lng, zoom]);

  // The poster frame resizes with the chosen format, and MapLibre needs telling.
  useEffect(() => {
    if (!container.current) return;
    const observer = new ResizeObserver(() => map.current?.resize());
    observer.observe(container.current);
    return () => observer.disconnect();
  }, []);

  // Absolute rather than h-full: the parent gets its height from flex, so a
  // percentage height resolves to zero and MapLibre then loads no tiles at all.
  return <div ref={container} className="absolute inset-0" />;
}
